import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { setAvoidAreaDrawing, setAvoidSegmentPicking, setAvoidSegmentGeometry } from 'actions';
import Checkbox from '../../Form/Checkbox';
import { colors, fontSize } from '../../../global/variables';
import {
  getAvoidFeaturesForFleetType,
  getAvoidAreaSummary,
  parseBoundingBoxArea,
  parsePolygonAreaInput,
  buildAvoidPayload,
  parseAvoidSegment,
} from '../../../utils/avoidAreaHelpers';

const { secondaryTextColor, primaryTextColor, primaryBorderColor, secondaryBorderColor, aqua } =
  colors;
const { small, smaller } = fontSize;

const TOOL_BBOX = 'bbox';
const TOOL_POLYGON = 'polygon';

const StyledSection = styled.details({
  backgroundColor: 'rgba(0,0,0,.025)',
  border: `1px solid ${secondaryBorderColor}`,
  borderRadius: '0.12rem',
  padding: '0.75rem',
  marginBottom: '0.9rem',
});

const StyledSummary = styled.summary({
  fontSize: small,
  fontWeight: 700,
  color: '#9C6B00',
  cursor: 'pointer',
});

const StyledSubTitle = styled.div({
  fontSize: smaller,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: secondaryTextColor,
  marginTop: '0.75rem',
  marginBottom: '0.4rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const StyledDrawButton = styled.button({
  background: 'transparent',
  color: primaryTextColor,
  border: `1px solid ${primaryBorderColor}`,
  borderRadius: '0.12rem',
  padding: '0.22rem 0.55rem',
  fontSize: smaller,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'FiraGO',
  ':hover': { borderColor: aqua, color: aqua },
});

const StyledAreaTag = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.3rem',
  padding: '0.18rem 0.5rem 0.18rem 0.38rem',
  borderRadius: '1.5rem',
  fontSize: smaller,
  fontWeight: 500,
  margin: '0.15rem 0.3rem 0.15rem 0',
  backgroundColor: 'rgba(255,102,102,.1)',
  border: '1px solid rgba(255,102,102,.35)',
  color: '#C83C3C',
});

const StyledAreaTagRemove = styled.button({
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'inherit',
  opacity: 0.6,
  fontSize: smaller,
  padding: 0,
  lineHeight: 1,
  ':hover': { opacity: 1 },
});

const StyledEmptyLabel = styled.div({
  fontSize: smaller,
  color: primaryTextColor,
  fontStyle: 'italic',
  marginBottom: '0.4rem',
});

const StyledDrawPanel = styled.div({
  border: `1px solid ${secondaryBorderColor}`,
  borderRadius: '0.12rem',
  backgroundColor: '#fff',
  padding: '0.65rem',
  marginTop: '0.4rem',
  marginBottom: '0.5rem',
});

const StyledToolTabs = styled.div({
  display: 'flex',
  gap: '0.3rem',
  marginBottom: '0.5rem',
});

const StyledToolTab = styled.button(({ active }) => ({
  background: active ? aqua : 'transparent',
  color: active ? '#fff' : primaryTextColor,
  border: `1px solid ${active ? aqua : primaryBorderColor}`,
  borderRadius: '1.5rem',
  padding: '0.2rem 0.6rem',
  fontSize: smaller,
  fontWeight: 700,
  textTransform: 'uppercase',
  cursor: 'pointer',
  fontFamily: 'FiraGO',
}));

const StyledCoordGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0.5rem',
  marginBottom: '0.5rem',
});

const StyledCoordField = styled.div({
  display: 'flex',
  flexDirection: 'column',
});

const StyledCoordLabel = styled.label({
  fontSize: smaller,
  color: secondaryTextColor,
  marginBottom: '0.2rem',
});

const StyledCoordInput = styled.input({
  fontSize: small,
  padding: '0.35rem 0.5rem',
  border: `1px solid ${primaryBorderColor}`,
  borderRadius: '0.12rem',
  fontFamily: 'FiraGO',
});

const StyledPolygonTextarea = styled.textarea({
  width: '100%',
  minHeight: '5rem',
  fontSize: small,
  padding: '0.4rem 0.5rem',
  border: `1px solid ${primaryBorderColor}`,
  borderRadius: '0.12rem',
  fontFamily: 'FiraGO',
  marginBottom: '0.4rem',
  resize: 'vertical',
});

const StyledError = styled.div({
  color: colors.negativeTextColor,
  fontSize: smaller,
  marginBottom: '0.4rem',
});

const StyledDrawPanelFooter = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '0.4rem',
});

const StyledSmallButton = styled.button({
  fontFamily: 'FiraGO',
  fontSize: smaller,
  fontWeight: 500,
  padding: '0.3rem 0.7rem',
  borderRadius: '0.12rem',
  cursor: 'pointer',
});

const StyledSmallCancelButton = styled(StyledSmallButton)({
  background: 'transparent',
  color: primaryTextColor,
  border: `1px solid ${primaryBorderColor}`,
});

const StyledSmallApplyButton = styled(StyledSmallButton)({
  background: aqua,
  color: '#fff',
  border: 'none',
});

const StyledFeatGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0.25rem 0.5rem',
});

const StyledManualEntryToggle = styled.button({
  background: 'transparent',
  border: 'none',
  color: aqua,
  fontSize: smaller,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'FiraGO',
  padding: 0,
  marginBottom: '0.4rem',
});

const StyledSegmentTag = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.3rem',
  padding: '0.18rem 0.5rem 0.18rem 0.38rem',
  borderRadius: '1.5rem',
  fontSize: smaller,
  fontWeight: 500,
  margin: '0.15rem 0.3rem 0.15rem 0',
  backgroundColor: 'rgba(255,140,66,.1)',
  border: '1px solid rgba(255,140,66,.4)',
  color: 'rgba(180,90,0,.9)',
});

const StyledSegmentId = styled.code({
  fontSize: '.6rem',
});

const StyledSegmentNoPreview = styled.span({
  fontSize: '.65rem',
  cursor: 'help',
});

const DIRECTION_ARROWS = { both: '\u2194', forward: '\u2192', backward: '\u2190' };

const AvoidAreasEditor = ({
  fleetType,
  avoid,
  onChange,
  labels: {
    avoidAreasRoadsTitle,
    avoidAreasSectionTitle,
    avoidRoadsSectionTitle,
    drawOnMapLabel,
    avoidAreaBboxTab,
    avoidAreaPolygonTab,
    avoidAreaNorthLabel,
    avoidAreaSouthLabel,
    avoidAreaEastLabel,
    avoidAreaWestLabel,
    avoidAreaPolygonPlaceholder,
    avoidAreaApplyLabel,
    avoidAreaCancelLabel,
    avoidAreaInvalidError,
    avoidAreaEmptyLabel,
    avoidAreaRemoveTooltip,
    avoidAreaManualEntryToggleLabel,
    avoidFeatures,
    avoidSegmentsSectionTitle,
    avoidSegmentPickOnMapLabel,
    avoidSegmentEmptyLabel,
    avoidSegmentRemoveTooltip,
    avoidSegmentNoPreviewTooltip,
  },
}) => {
  const [areas, setAreas] = useState(avoid?.areas || []);
  const [features, setFeatures] = useState(avoid?.features || []);
  const [segments, setSegments] = useState(
    (avoid?.segments || []).map(parseAvoidSegment).filter(Boolean),
  );
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState(TOOL_BBOX);
  const [bboxInput, setBboxInput] = useState({ north: '', south: '', east: '', west: '' });
  const [polygonInput, setPolygonInput] = useState('');
  const [error, setError] = useState(null);

  const dispatch = useDispatch();
  const mapDrawingShape = useSelector((state) => state.avoidAreaDrawing.shape);
  const confirmedSegment = useSelector((state) => state.avoidSegmentPicking.confirmed);
  const segmentGeometries = useSelector((state) => state.avoidSegmentGeometries);

  useEffect(() => {
    onChange(buildAvoidPayload({ areas, features, segments }));
  }, [areas, features, segments, onChange]);

  useEffect(() => {
    if (!mapDrawingShape) return;
    setAreas((prev) => [...prev, mapDrawingShape]);
    dispatch(setAvoidAreaDrawing({ shape: null }));
  }, [mapDrawingShape, dispatch]);

  useEffect(() => {
    if (!confirmedSegment) return;
    const { id, direction } = confirmedSegment;
    setSegments((prev) => [...prev, { id, direction }]);
    if (confirmedSegment.coordinates) {
      dispatch(setAvoidSegmentGeometry(id, confirmedSegment.coordinates));
    }
    dispatch(setAvoidSegmentPicking({ confirmed: null }));
  }, [confirmedSegment, dispatch]);

  const availableFeatures = useMemo(() => getAvoidFeaturesForFleetType(fleetType), [fleetType]);

  const onStartMapDrawing = useCallback(() => {
    dispatch(setAvoidAreaDrawing({ active: true, tool: TOOL_BBOX, shape: null }));
  }, [dispatch]);

  const onStartSegmentPicking = useCallback(() => {
    dispatch(setAvoidSegmentPicking({ active: true, status: 'idle', segment: null, error: null }));
  }, [dispatch]);

  const onRemoveSegment = useCallback(
    (index) => setSegments((prev) => prev.filter((_, i) => i !== index)),
    [],
  );

  const onToggleDraw = useCallback(() => {
    setIsDrawing((prev) => !prev);
    setError(null);
  }, []);

  const onBboxFieldChange = useCallback(
    (field) => (e) => setBboxInput((prev) => ({ ...prev, [field]: e.target.value })),
    [],
  );

  const onPolygonInputChange = useCallback((e) => setPolygonInput(e.target.value), []);

  const onApplyArea = useCallback(() => {
    const area =
      tool === TOOL_BBOX ? parseBoundingBoxArea(bboxInput) : parsePolygonAreaInput(polygonInput);
    if (!area) {
      setError(avoidAreaInvalidError);
      return;
    }
    setAreas((prev) => [...prev, area]);
    setBboxInput({ north: '', south: '', east: '', west: '' });
    setPolygonInput('');
    setError(null);
    setIsDrawing(false);
  }, [tool, bboxInput, polygonInput, avoidAreaInvalidError]);

  const onRemoveArea = useCallback(
    (index) => setAreas((prev) => prev.filter((_, i) => i !== index)),
    [],
  );

  const onToggleFeature = useCallback((feature) => {
    setFeatures((prev) =>
      prev.includes(feature) ? prev.filter((f) => f !== feature) : [...prev, feature],
    );
  }, []);

  return (
    <StyledSection>
      <StyledSummary>{avoidAreasRoadsTitle}</StyledSummary>
      <StyledSubTitle>
        <span>{avoidAreasSectionTitle}</span>
        <StyledDrawButton
          type="button"
          id="button-fleet-profile-avoid-draw-on-map"
          onClick={onStartMapDrawing}
        >
          {drawOnMapLabel}
        </StyledDrawButton>
      </StyledSubTitle>
      {areas.length === 0 && <StyledEmptyLabel>{avoidAreaEmptyLabel}</StyledEmptyLabel>}
      {areas.map((area, index) => (
        <StyledAreaTag key={`${area.type}-${index}`}>
          {getAvoidAreaSummary(area)}
          <StyledAreaTagRemove
            type="button"
            id={`button-fleet-profile-avoid-area-remove-${index}`}
            title={avoidAreaRemoveTooltip}
            onClick={() => onRemoveArea(index)}
          >
            ×
          </StyledAreaTagRemove>
        </StyledAreaTag>
      ))}
      {!isDrawing && (
        <StyledManualEntryToggle
          type="button"
          id="button-fleet-profile-avoid-manual-entry-toggle"
          onClick={onToggleDraw}
        >
          {avoidAreaManualEntryToggleLabel}
        </StyledManualEntryToggle>
      )}
      {isDrawing && (
        <StyledDrawPanel>
          <StyledToolTabs>
            <StyledToolTab
              type="button"
              active={tool === TOOL_BBOX}
              onClick={() => setTool(TOOL_BBOX)}
            >
              {avoidAreaBboxTab}
            </StyledToolTab>
            <StyledToolTab
              type="button"
              active={tool === TOOL_POLYGON}
              onClick={() => setTool(TOOL_POLYGON)}
            >
              {avoidAreaPolygonTab}
            </StyledToolTab>
          </StyledToolTabs>
          {tool === TOOL_BBOX ? (
            <StyledCoordGrid>
              <StyledCoordField>
                <StyledCoordLabel htmlFor="input-fleet-profile-avoid-bbox-north">
                  {avoidAreaNorthLabel}
                </StyledCoordLabel>
                <StyledCoordInput
                  id="input-fleet-profile-avoid-bbox-north"
                  type="number"
                  value={bboxInput.north}
                  onChange={onBboxFieldChange('north')}
                />
              </StyledCoordField>
              <StyledCoordField>
                <StyledCoordLabel htmlFor="input-fleet-profile-avoid-bbox-south">
                  {avoidAreaSouthLabel}
                </StyledCoordLabel>
                <StyledCoordInput
                  id="input-fleet-profile-avoid-bbox-south"
                  type="number"
                  value={bboxInput.south}
                  onChange={onBboxFieldChange('south')}
                />
              </StyledCoordField>
              <StyledCoordField>
                <StyledCoordLabel htmlFor="input-fleet-profile-avoid-bbox-east">
                  {avoidAreaEastLabel}
                </StyledCoordLabel>
                <StyledCoordInput
                  id="input-fleet-profile-avoid-bbox-east"
                  type="number"
                  value={bboxInput.east}
                  onChange={onBboxFieldChange('east')}
                />
              </StyledCoordField>
              <StyledCoordField>
                <StyledCoordLabel htmlFor="input-fleet-profile-avoid-bbox-west">
                  {avoidAreaWestLabel}
                </StyledCoordLabel>
                <StyledCoordInput
                  id="input-fleet-profile-avoid-bbox-west"
                  type="number"
                  value={bboxInput.west}
                  onChange={onBboxFieldChange('west')}
                />
              </StyledCoordField>
            </StyledCoordGrid>
          ) : (
            <StyledPolygonTextarea
              id="input-fleet-profile-avoid-polygon"
              placeholder={avoidAreaPolygonPlaceholder}
              value={polygonInput}
              onChange={onPolygonInputChange}
            />
          )}
          {error && <StyledError>{error}</StyledError>}
          <StyledDrawPanelFooter>
            <StyledSmallCancelButton
              type="button"
              id="button-fleet-profile-avoid-draw-cancel"
              onClick={onToggleDraw}
            >
              {avoidAreaCancelLabel}
            </StyledSmallCancelButton>
            <StyledSmallApplyButton
              type="button"
              id="button-fleet-profile-avoid-draw-apply"
              onClick={onApplyArea}
            >
              {avoidAreaApplyLabel}
            </StyledSmallApplyButton>
          </StyledDrawPanelFooter>
        </StyledDrawPanel>
      )}
      <StyledSubTitle>
        <span>{avoidSegmentsSectionTitle}</span>
        <StyledDrawButton
          type="button"
          id="button-fleet-profile-avoid-segment-pick"
          onClick={onStartSegmentPicking}
        >
          {avoidSegmentPickOnMapLabel}
        </StyledDrawButton>
      </StyledSubTitle>
      {segments.length === 0 && <StyledEmptyLabel>{avoidSegmentEmptyLabel}</StyledEmptyLabel>}
      {segments.map((segment, index) => (
        <StyledSegmentTag key={`${segment.id}-${index}`}>
          {'\uD83D\uDEE3 '}
          <StyledSegmentId>{segment.id}</StyledSegmentId>
          {DIRECTION_ARROWS[segment.direction] || DIRECTION_ARROWS.both}
          {!segmentGeometries[segment.id] && (
            <StyledSegmentNoPreview title={avoidSegmentNoPreviewTooltip}>⚠</StyledSegmentNoPreview>
          )}
          <StyledAreaTagRemove
            type="button"
            id={`button-fleet-profile-avoid-segment-remove-${index}`}
            title={avoidSegmentRemoveTooltip}
            onClick={() => onRemoveSegment(index)}
          >
            ×
          </StyledAreaTagRemove>
        </StyledSegmentTag>
      ))}
      <StyledSubTitle>{avoidRoadsSectionTitle}</StyledSubTitle>
      <StyledFeatGrid>
        {availableFeatures.map((feature) => (
          <Checkbox
            key={feature}
            id={`checkbox-fleet-profile-avoid-feature-${feature}`}
            value={features.includes(feature)}
            label={avoidFeatures[feature]}
            onChange={() => onToggleFeature(feature)}
          />
        ))}
      </StyledFeatGrid>
    </StyledSection>
  );
};

export default AvoidAreasEditor;
